import React, { useEffect, useState } from 'react';
import {
  FaAddressCard,
  FaFilePdf,
  FaGlobe,
  FaPhone,
  FaWhatsapp
} from 'react-icons/fa';
import AboutContact from './components/AboutContact';
import DesignPanel from './components/DesignPanel';
import Header from './components/Header';
import InputForms from './components/InputForms';
import QRPreview from './components/QRPreview';
import SEOContent from './components/SEOContent';
import SocialLinks from './components/SocialLinks';
import StarryBackground from './components/StarryBackground';
import { getRandomColor } from './utils/qr-helper';

const tabs = [
  { id: 'website', icon: FaGlobe, label: '網站' },
  { id: 'pdf', icon: FaFilePdf, label: 'PDF' },
  { id: 'contact', icon: FaAddressCard, label: '聯絡人' },
  { id: 'whatsapp', icon: FaWhatsapp, label: 'WhatsApp' },
  { id: 'phone', icon: FaPhone, label: '電話' }
];

function App() {
  const [activeTab, setActiveTab] = useState('website');
  const [formData, setFormData] = useState({
    url: 'https://',
    pdfUrl: '',
    name: '',
    phone: '',
    email: '',
    waNumber: '',
    waMessage: '',
    tel: ''
  });
  const [qrConfig, setQrConfig] = useState({
    dotStyle: 'rounded',
    cornerStyle: 'rounded',
    fgColor: '#6366f1',
    bgColor: '#FFFFFF',
    logo: null
  });
  const [qrContent, setQrContent] = useState('');
  const [isMagicLoading, setIsMagicLoading] = useState(false);

  useEffect(() => {
    let content = '';

    switch (activeTab) {
      case 'website':
        content = formData.url || 'https://qr.homy.hk';
        break;
      case 'pdf':
        content = formData.pdfUrl;
        break;
      case 'contact':
        if (formData.name || formData.phone) {
          content = `BEGIN:VCARD\r\nVERSION:3.0\r\nFN:${formData.name}\r\nTEL;TYPE=CELL:${formData.phone}\r\nEMAIL:${formData.email}\r\nEND:VCARD`;
        }
        break;
      case 'whatsapp':
        if (formData.waNumber) {
          const cleanPhone = formData.waNumber.replace(/\D/g, '');
          const message = formData.waMessage
            ? `?text=${encodeURIComponent(formData.waMessage)}`
            : '';
          content = `https://wa.me/${cleanPhone}${message}`;
        }
        break;
      case 'phone':
        if (formData.tel) {
          content = `tel:${formData.tel.replace(/\s/g, '')}`;
        }
        break;
      default:
        break;
    }

    setQrContent(content);
  }, [formData, activeTab]);

  const handleMagicColor = () => {
    if (isMagicLoading) return;

    setIsMagicLoading(true);
    setTimeout(() => {
      const styles = ['square', 'rounded', 'dots'];
      setQrConfig((previous) => ({
        ...previous,
        fgColor: getRandomColor(),
        bgColor: '#FFFFFF',
        dotStyle: styles[Math.floor(Math.random() * styles.length)],
        cornerStyle: 'rounded'
      }));
      setIsMagicLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center py-8">
      <StarryBackground />
      <Header />

      <main className="w-full max-w-6xl px-4 z-10 relative flex flex-col lg:flex-row gap-6">
        <article className="flex-1 flex flex-col gap-6">
          <section className="glass-card overflow-hidden">
            <nav className="flex overflow-x-auto border-b border-white/10 p-2 gap-1 no-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-primary/20 text-white border border-primary/30 shadow-lg shadow-primary/20'
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <tab.icon aria-hidden="true" />
                  {tab.label}
                </button>
              ))}
            </nav>

            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                  1
                </span>
                <h2 className="text-xl font-bold text-white">
                  您想製作哪種 QR Code？
                </h2>
              </div>
              <InputForms
                type={activeTab}
                data={formData}
                onChange={setFormData}
              />
            </div>
          </section>

          <section className="glass-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                2
              </span>
              <h2 className="text-xl font-bold text-white">
                如何自訂 QR Code 設計？
              </h2>
            </div>
            <DesignPanel
              config={qrConfig}
              setConfig={setQrConfig}
              onMagicClick={handleMagicColor}
            />
          </section>
        </article>

        <aside className="lg:w-[350px] flex-shrink-0">
          <div className="glass-card p-6 sticky top-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                3
              </span>
              <h2 className="text-xl font-bold text-white">
                如何預覽及下載 QR Code？
              </h2>
            </div>
            <QRPreview
              content={qrContent}
              config={qrConfig}
              isMagicLoading={isMagicLoading}
            />
          </div>
        </aside>
      </main>

      <SEOContent />
      <AboutContact />

      <footer className="relative z-10 mt-16 pb-12 text-slate-500 text-sm flex flex-col items-center gap-4">
        <nav
          className="flex items-center gap-5"
          aria-label="網站資訊"
        >
          <a
            href="#about"
            className="hover:text-primary-light transition-colors"
          >
            關於 QR星球
          </a>
          <a
            href="#contact"
            className="hover:text-primary-light transition-colors"
          >
            聯絡我們
          </a>
        </nav>

        <p className="opacity-60">
          © 2026 QR星球 (QR Planet) · 專業免費二維碼產生器
        </p>

        <SocialLinks />

        <div className="flex items-center gap-1.5 tracking-wide mt-2">
          <span className="text-slate-600">Crafted by</span>
          <a
            href="https://homy.hk"
            target="_blank"
            rel="noopener noreferrer"
            title="Homy Tech - 專業技術解決方案與網頁設計"
            className="text-slate-400 hover:text-primary transition-all duration-300 no-underline font-semibold hover:scale-105"
          >
            Homy Tech
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;